import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';

import { debounceTime, distinctUntilChanged, filter, map, noop, Observable, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { FeatureFlag } from '@core/enums';
import { Dictionary } from '@shared/models';
import { RelatedToType } from '@shared/enums';
import { AccountState } from '@state/account/state';
import { SavedViewsState } from '@state/saved-view/state';
import { SavedViewsActions } from '@state/saved-view/action';
import { PermissionUtils } from '@core/utils/permission.utils';
import { FieldDef, SavedView } from '@core/models/account.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { CustomObject, CustomObjectValue } from '../../modules/custom-objects/custom-objects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { CustomField, CustomFormField } from '@shared/modules/custom-form/custom-form.types';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';
import { CustomObjectsService } from '../../../services/custom-objects.service';
import { LookupService } from '@services/lookup.service';
import { LayoutsService } from '@services/layouts.service';

declare interface DialogData {
  customObjectType: Pick<CustomObject, 'name'>['name'];
  customObjectId: Pick<CustomObject, 'id'>['id'];
  related_to_type: RelatedToType;
  model: any;
  canAddNewRecord: boolean;
  creationMode?: boolean;
  searchTerm?: string;
}

/**
 * Interface defining the contract for handling different types of searches in the component.
 * Each search type (Property, Project, CustomObject) implements this interface to provide
 * its specific search and response processing logic.
 */
declare interface SearchHandler {
  /**
   * Returns an Observable that performs the search operation for the given value.
   * @param value - The search term entered by the user
   * @returns Observable that emits the search results
   */
  getObservable: (value: string) => Observable<any>;
  /**
   * Processes the raw response from the search operation and transforms it into
   * the format expected by the component.
   * @param response - The raw response from the search operation
   * @returns Array of processed search results
   */
  processResponse: (response: any) => any[];
}

@Component({
  selector: 'add-link-to-custom-object-value',
  templateUrl: './add-link-to-custom-object-value.component.html',
  styleUrls: ['./add-link-to-custom-object-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLinkToCustomObjectValueDialog {
  addLinkToCOValueForm: FormGroup;
  createLinkToCOValueForm: FormGroup;
  customObjects: CustomObject[];
  customObjectOptions: any[];
  customObjectType: Pick<CustomObject, 'name'>['name'];
  customObjectId: Pick<CustomObject, 'id'>['id'];
  customObject: CustomObject;
  customObjectSearchFieldLabel: string;
  isNewRecord = signal(false);
  canAddNewRecord = signal(false);
  savedViews = signal<SavedView[]>([]);
  fieldsFromSavedView = signal<CustomFormField[]>([]);
  formValues = signal<Dictionary<any>>({});
  templates = signal<ProjectTemplate<any>[]>([]);
  defaultFieldDefs: FieldDef[];
  canSave = signal(false);
  hasSearched = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  private customObjectsService = inject(CustomObjectsService);

  private get$: Observable<CustomObject[]> = inject(Store).select(ObjectsManagerState.get);

  /**
   * Mapping of search types to their respective handlers.
   * Each handler implements the SearchHandler interface to provide type-specific
   * search and response processing logic.
   */
  private readonly searchHandlers: Record<string, SearchHandler> = {
    [RelatedToType.Property]: {
      getObservable: (value: string) =>
        this.propertiesService.getProperties(
          ['id', 'name'],
          {},
          0,
          20,
          'name',
          'asc',
          value,
          '',
          '',
          this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          ['name'],
        ),
      processResponse: (response: { properties: Property[] }) => response?.properties,
    },
    [RelatedToType.Project]: {
      getObservable: (value: string) =>
        this.projectsService.getProjectsNoPipeline(
          ['id', 'name'],
          {},
          0,
          20,
          'title',
          'asc',
          value,
          false,
          null,
          false,
          null,
          this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          ['name', 'title'],
        ),
      processResponse: (response: { projects: Project[] }) => response?.projects,
    },
  };

  private get accountId(): number {
    return Number(this.accountService.getCurrentAccount()?.id);
  }

  private get isCreateFormValid(): boolean {
    return Object.entries(this.formValues()).length > 0 && Object.keys(this.formValues()).length > 0;
  }

  private get isFormValid(): boolean {
    return this.addLinkToCOValueForm.valid && this.canSave();
  }

  get isSaveButtonDisabled(): boolean {
    return this.isNewRecord() ? !this.isCreateFormValid : !this.isFormValid;
  }

  get noResultsFound(): boolean {
    return this.hasSearched() && !this.customObjectOptions?.length;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public matDialogRef: MatDialogRef<AddLinkToCustomObjectValueDialog>,
    private readonly formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
    private readonly accountService: AccountService,
    private readonly propertiesService: PropertiesService,
    private readonly projectsService: ProjectsService,
    private readonly layoutsService: LayoutsService,
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly lookupService: LookupService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new ObjectsManagerActions.Get());
    this.customObjectType = this.data?.customObjectType;
    this.customObjectId = Number(this.data?.customObjectId);
    this.canAddNewRecord.set(this.data?.canAddNewRecord);
    this.buildForm();

    if (this.customObjectId) {
      this.objectsManagerService
        .getById(this.accountId, this.customObjectId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((customObject) => {
          this.customObject = customObject;
          this.customObjectSearchFieldLabel = customObject?.custom_fields?.find(
            (field) => field.id === customObject?.search_custom_field_id,
          )?.label;

          if (this.data?.creationMode) {
            this.startCreateMode();
            this.prefillSearchTerm();
          }

          this.changeDetectorRef.markForCheck();
        });
    }

    this.get$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((objects) => {
          this.customObjects = objects.filter((objectItem) => objectItem?.id);
        }),
      )
      .subscribe(noop);

    this.buildForm();

    if (this.data?.searchTerm) {
      this.addLinkToCOValueForm.get('name').setValue(this.data.searchTerm, { emitEvent: false });
    }

    this.setupValueChanges();
  }

  /**
   * Sets up the form's value changes subscription to handle search operations.
   * This method configures the RxJS pipeline that processes user input,
   * performs debouncing, and handles the search results.
   */
  private setupValueChanges(): void {
    this.addLinkToCOValueForm
      .get('name')
      .valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value) => {
          if (!value) {
            this.customObjectOptions = [];
            this.canSave.set(false);
            this.hasSearched.set(false);
          } else {
            this.hasSearched.set(true);
            // Only reset canSave if this is a manual input (not from autocomplete selection)
            if (!this.customObjectOptions?.some((option) => option.name === value)) {
              this.canSave.set(false);
            }
          }
          this.changeDetectorRef.markForCheck();
        }),
        filter((value) => !!value),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((value) => this.getSearchObservable(value)),
      )
      .subscribe((response) => {
        this.customObjectOptions = this.processSearchResponse(response);
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Determines and returns the appropriate search Observable based on the current search type.
   * For custom objects, it uses the customObjectsService. For standard types (Property, Project),
   * it uses the corresponding handler from searchHandlers.
   * @param value - The search term entered by the user
   * @returns Observable that will emit the search results
   */
  private getSearchObservable(value: string): Observable<any> {
    if (this.isCustomObject(this.customObjectType)) {
      return this.lookupService.lookFor({ query: value, relatedTo: this.customObjectType });
    }

    const handler = this.searchHandlers[this.customObjectType];
    return handler ? handler.getObservable(value) : of([]);
  }

  /**
   * Processes the raw search response and transforms it into the format expected by the component.
   * For custom objects, it maps the values using the search field name. For standard types,
   * it uses the corresponding handler's processResponse method.
   * @param response - The raw response from the search operation
   * @returns Array of processed search results in the format { id: number, name: string }
   */
  private processSearchResponse(response: any): any[] {
    if (this.isCustomObject(this.customObjectType)) {
      return response.map((coValue) => ({ id: coValue.id, name: coValue.label }));
    }

    const handler = this.searchHandlers[this.customObjectType];
    return handler ? handler.processResponse(response) : null;
  }

  private buildForm(): void {
    this.addLinkToCOValueForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required]],
    });

    this.createLinkToCOValueForm = this.formBuilder.group({
      saved_view_id: [null],
      template_id: [null],
    });
  }

  isCustomObject(name: string): boolean {
    return this.customObjects?.some((co) => co.name === name);
  }

  private fetchSavedViews(): void {
    this.store.dispatch(new SavedViewsActions.LoadViews());

    this.store
      .select(SavedViewsState.getViews)
      .pipe(
        map((savedViews) => {
          return savedViews
            ? savedViews.filter((savedView) => savedView.related_to_type === this.data.customObjectType)
            : [];
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((filteredViews) => {
        this.savedViews.set(filteredViews);
        if (
          this.isNewRecord() &&
          filteredViews?.length > 0 &&
          !this.createLinkToCOValueForm.get('saved_view_id').value
        ) {
          const firstId = filteredViews[0].id;
          this.createLinkToCOValueForm.get('saved_view_id').setValue(firstId);
          this.selectSavedViewById(firstId);
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private fetchLayouts(): void {
    this.layoutsService
      .get<ProjectTemplate<any>>(this.customObject.name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((result: ProjectTemplate<any>[]) => {
          return result.sort((a, b) => {
            if (a.rank < b.rank) return -1;
            if (a.rank > b.rank) return 1;
            return 0;
          });
        }),
      )
      .subscribe((result) => {
        this.templates.set(result);
        this.createLinkToCOValueForm.get('template_id').setValue(this.templates()[0].id);
      });
  }

  private fetchFieldDefs(): void {
    this.defaultFieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(this.customObject.name, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
  }

  private toCustomFormField(customField: CustomField) {
    const { name: fieldName } = customField;
    return {
      name: fieldName,
      fieldDef: this.defaultFieldDefs.find(({ name }) => name === fieldName),
      ...customField,
    };
  }

  addNew(): void {
    this.startCreateMode();
    this.prefillSearchTerm();
  }

  private startCreateMode(): void {
    this.isNewRecord.set(true);
    this.fetchFieldDefs();
    this.fetchLayouts();
    this.fetchSavedViews();
  }

  private prefillSearchTerm(): void {
    const searchTerm = this.data?.searchTerm?.trim();
    if (!searchTerm || !this.customObject) {
      return;
    }

    const searchField = this.customObject?.custom_fields?.find(
      (field) => field.id === this.customObject?.search_custom_field_id,
    );

    if (searchField?.name) {
      this.formValues.update((prev) => ({ ...prev, [searchField.name]: searchTerm }));
    }
  }

  onSavedViewChange(event: MatSelectChange): void {
    this.selectSavedViewById(event.value as number);
  }

  private selectSavedViewById(savedViewId: number): void {
    const selectedSavedView = this.savedViews().find((savedView) => savedView.id === savedViewId);

    if (!selectedSavedView) {
      return;
    }

    let savedViewFieldsDefinition: Pick<SavedView, 'definition'>['definition'];

    const parsedDefinition = JSON.parse(selectedSavedView.definition || '{}');
    const customObjectFormDefinition = parsedDefinition?.custom_object_form_definition;

    if (customObjectFormDefinition) {
      savedViewFieldsDefinition = JSON.parse(customObjectFormDefinition)?.fields?.flat();
    }

    // If no form definition found or parsing failed, use custom object fields
    if (!savedViewFieldsDefinition) {
      savedViewFieldsDefinition = this.defaultFieldDefs;
    }

    // Set the fields from the saved view and remove linked ones
    this.fieldsFromSavedView.set(
      savedViewFieldsDefinition
        .map((field: CustomField) => this.toCustomFormField(field))
        .filter((field: CustomFormField) => field?.fieldDef?.type !== 'record'),
    );

    // Initialize formValues with empty object and clear the form when a new saved view is selected
    this.formValues.set({});
  }

  onFieldUpdate(event: Dictionary<any>): void {
    this.formValues.update((prev) => ({ ...prev, ...event }));
  }

  autocompleteSelected(event: MatAutocompleteSelectedEvent): void {
    this.canSave.set(true);
    this.addLinkToCOValueForm.patchValue({
      id: Number(event.option.id),
      name: event.option.value,
    });
  }

  saveAndClose(): void {
    if (this.isNewRecord()) {
      this.createLinkToCOValueForm.disable();

      /**
       * [SC-48695] WIP:
       * BE service is not ready to handle value updates at customObjectValue top-level attributes.
       * That's why we need to pass the customObjectValue as is inside the data attribute,
       * and the BE will handle the modifications at the customObjectValue level.
       */

      const customObjectValue: CustomObjectValue = {
        // TODO: Remove below once BE is ready to handle value updates at customObjectValue top-level attributes.
        data: { ...this.formValues() },
        // TODO: Uncomment below once BE is ready to handle value updates at customObjectValue top-level attributes.
        // ...this.formValues(),
        related_to_type: this.data.related_to_type,
        related_to_id: this.data.model.id,
        template_id: this.createLinkToCOValueForm.value.template_id,
      };

      this.customObjectsService
        .createValue(+this.store.selectSnapshot(AccountState.getAccountId), this.customObjectId, customObjectValue)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((value) => {
            this.matDialogRef.close({ model: { ...value } });
          }),
        )
        .subscribe(noop);
    } else {
      const coValue = this.addLinkToCOValueForm.value;
      this.addLinkToCOValueForm.disable();
      this.matDialogRef.close({ model: coValue, addedExistedRecord: true });
    }
  }

  close(): void {
    this.matDialogRef.close();
  }

  getCreateCustomObjectPermission(customObjectName: string): string {
    return PermissionUtils.generateCustomObjectPermissions(customObjectName).CREATE;
  }
}
